/**
 * G2 Behavioral Profile (G2BP) - Teste Comportamental
 * 
 * Formato: 40 questões com 4 palavras cada
 * O usuário seleciona: 1 palavra MAIS identificada + 1 palavra MENOS identificada
 * 
 * Mapeamento:
 * - Palavra 1 = D (Dominância)
 * - Palavra 2 = I (Influência)
 * - Palavra 3 = S (Estabilidade)
 * - Palavra 4 = C (Conformidade)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  Loader2,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import { candidatesAPI, testInvitesAPI } from '../../lib/api';

// 40 questões G2BP - Formato de palavras
const G2BP_QUESTIONS = [
  { id: 1, words: ['Determinado', 'Comunicativo', 'Paciente', 'Preciso'] },
  { id: 2, words: ['Competitivo', 'Inspirador', 'Prestativo', 'Organizado'] },
  { id: 3, words: ['Decidido', 'Entusiasmado', 'Cooperativo', 'Analítico'] },
  { id: 4, words: ['Corajoso', 'Sociável', 'Tranquilo', 'Cuidadoso'] },
  { id: 5, words: ['Assertivo', 'Persuasivo', 'Gentil', 'Metódico'] },
  { id: 6, words: ['Ousado', 'Carismático', 'Constante', 'Disciplinado'] },
  { id: 7, words: ['Independente', 'Alegre', 'Compreensivo', 'Lógico'] },
  { id: 8, words: ['Desafiador', 'Motivador', 'Atencioso', 'Perfeccionista'] },
  { id: 9, words: ['Líder', 'Convincente', 'Equilibrado', 'Sistemático'] },
  { id: 10, words: ['Ambicioso', 'Extrovertido', 'Leal', 'Responsável'] },
  { id: 11, words: ['Rápido', 'Popular', 'Sereno', 'Minucioso'] },
  { id: 12, words: ['Firme', 'Otimista', 'Diplomático', 'Exato'] },
  { id: 13, words: ['Resoluto', 'Animado', 'Calmo', 'Criterioso'] },
  { id: 14, words: ['Persistente', 'Influente', 'Estável', 'Técnico'] },
  { id: 15, words: ['Direto', 'Simpático', 'Flexível', 'Planejador'] },
  { id: 16, words: ['Executor', 'Convincente', 'Solidário', 'Racional'] },
  { id: 17, words: ['Inovador', 'Criativo', 'Confiável', 'Detalhista'] },
  { id: 18, words: ['Objetivo', 'Comunicador', 'Humilde', 'Prudente'] },
  { id: 19, words: ['Incisivo', 'Inspirador', 'Cuidadoso', 'Conservador'] },
  { id: 20, words: ['Empreendedor', 'Espontâneo', 'Harmonioso', 'Meticuloso'] },
  { id: 21, words: ['Destemido', 'Amigável', 'Prestativo', 'Organizado'] },
  { id: 22, words: ['Competidor', 'Empático', 'Pacífico', 'Estruturado'] },
  { id: 23, words: ['Resolvido', 'Falante', 'Constante', 'Investigativo'] },
  { id: 24, words: ['Determinado', 'Divertido', 'Tolerante', 'Precavido'] },
  { id: 25, words: ['Autoconfiante', 'Expressivo', 'Cooperador', 'Sistemático'] },
  { id: 26, words: ['Dominante', 'Influenciador', 'Estável', 'Metódico'] },
  { id: 27, words: ['Incansável', 'Alegre', 'Paciente', 'Analítico'] },
  { id: 28, words: ['Atrevido', 'Comunicador', 'Discreto', 'Organizado'] },
  { id: 29, words: ['Enérgico', 'Convincente', 'Calmo', 'Exigente'] },
  { id: 30, words: ['Competente', 'Inspirador', 'Fiel', 'Planejado'] },
  { id: 31, words: ['Controlador', 'Sociável', 'Solidário', 'Preciso'] },
  { id: 32, words: ['Focado', 'Carismático', 'Tranquilo', 'Cuidadoso'] },
  { id: 33, words: ['Persistente', 'Entusiasta', 'Prestativo', 'Cauteloso'] },
  { id: 34, words: ['Resolutivo', 'Popular', 'Diplomático', 'Detalhista'] },
  { id: 35, words: ['Objetivo', 'Motivador', 'Leal', 'Organizado'] },
  { id: 36, words: ['Corajoso', 'Comunicativo', 'Estável', 'Lógico'] },
  { id: 37, words: ['Líder', 'Persuasivo', 'Cooperativo', 'Meticuloso'] },
  { id: 38, words: ['Audacioso', 'Inspirador', 'Paciente', 'Técnico'] },
  { id: 39, words: ['Prático', 'Alegre', 'Compreensivo', 'Sistemático'] },
  { id: 40, words: ['Determinado', 'Influente', 'Sereno', 'Perfeccionista'] }
];

// Mapeamento: índice da palavra -> traço DISC
const TRAIT_MAP = ['D', 'I', 'S', 'C'];

// Cores dos traços
const TRAIT_COLORS = {
  D: { bg: 'bg-red-500', text: 'text-red-500', light: 'bg-red-50', border: 'border-red-200' },
  I: { bg: 'bg-amber-500', text: 'text-amber-500', light: 'bg-amber-50', border: 'border-amber-200' },
  S: { bg: 'bg-green-500', text: 'text-green-500', light: 'bg-green-50', border: 'border-green-200' },
  C: { bg: 'bg-blue-500', text: 'text-blue-500', light: 'bg-blue-50', border: 'border-blue-200' }
};

// Perfis DISC
const DISC_PROFILES = {
  D: {
    nome: 'Dominância',
    descricao: 'Perfil orientado para resultados, desafios e tomada de decisão rápida.',
    caracteristicas: ['Determinado', 'Competitivo', 'Direto', 'Decisivo']
  },
  I: {
    nome: 'Influência',
    descricao: 'Perfil comunicativo, entusiasta e focado em relacionamentos.',
    caracteristicas: ['Comunicativo', 'Otimista', 'Persuasivo', 'Sociável']
  },
  S: {
    nome: 'Estabilidade',
    descricao: 'Perfil paciente, leal e focado em harmonia e cooperação.',
    caracteristicas: ['Paciente', 'Leal', 'Cooperativo', 'Tranquilo']
  },
  C: {
    nome: 'Conformidade',
    descricao: 'Perfil analítico, detalhista e focado em qualidade e precisão.',
    caracteristicas: ['Analítico', 'Preciso', 'Organizado', 'Sistemático']
  }
};

// Componente de gráfico de barras normalizado (0-100%)
function G2BPBars({ percentages, label, compact = false }) {
  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      {label && (
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
      )}
      {['D', 'I', 'S', 'C'].map((trait) => {
        const percentage = percentages[trait] || 0;
        const profile = DISC_PROFILES[trait];
        const colors = TRAIT_COLORS[trait];
        
        return (
          <div key={trait} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className={`font-medium ${colors.text}`}>
                {compact ? trait : `${trait} - ${profile.nome}`}
              </span>
              <span className="text-slate-600 font-semibold">{percentage}%</span>
            </div>
            <div className={`${compact ? 'h-3' : 'h-4'} bg-slate-100 rounded-full overflow-hidden`}>
              <div 
                className={`h-full ${colors.bg} transition-all duration-500 rounded-full`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Componente de alerta de inconsistência
function InconsistenciaAlert({ motivo }) {
  return (
    <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xl">⚠</span>
        </div>
        <div>
          <h4 className="font-bold text-amber-800">Perfil Sob Análise</h4>
          <p className="text-sm text-amber-700 mt-1">
            Este resultado apresenta padrões atípicos que podem indicar estresse ou inconsistência nas respostas.
          </p>
          <p className="text-xs text-amber-600 mt-2 italic">{motivo}</p>
        </div>
      </div>
    </div>
  );
}

export default function MobileG2BP() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [existingResult, setExistingResult] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);

  // Verificar se já tem resultado G2BP
  useEffect(() => {
    const checkExisting = async () => {
      try {
        const response = await candidatesAPI.getMe();
        if (response.data?.g2bp_result) {
          setExistingResult(response.data.g2bp_result);
        }
      } catch (error) {
        console.error('Erro ao verificar G2BP existente:', error);
      } finally {
        setCheckingExisting(false);
      }
    };
    checkExisting();
  }, []);

  const totalQuestions = G2BP_QUESTIONS.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  
  // Resposta atual da questão
  const currentAnswer = answers[currentQuestion] || { mais: null, menos: null };

  // Selecionar palavra
  const handleSelectWord = (type, wordIndex) => {
    const otherType = type === 'mais' ? 'menos' : 'mais';
    const currentOther = currentAnswer[otherType];
    
    // Se está selecionando a mesma palavra que já está no outro tipo, ignorar
    if (currentOther === wordIndex) {
      toast.error('Você não pode selecionar a mesma palavra para MAIS e MENOS');
      return;
    }
    
    setAnswers(prev => ({
      ...prev,
      [currentQuestion]: {
        ...prev[currentQuestion],
        [type]: wordIndex
      }
    }));
  };

  // Verificar se pode avançar
  const canProceed = currentAnswer.mais !== null && currentAnswer.menos !== null;

  // Próxima questão
  const handleNext = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      calculateResult();
    }
  };

  // Questão anterior
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  // Calcular resultado - Algoritmo DISC Ipsativo (Forced-Choice)
  const calculateResult = async () => {
    setLoading(true);
    
    try {
      // ==========================================
      // PASSO 1: Calcular as 3 Matrizes Separadas
      // ==========================================
      const totalQuestoes = Object.keys(answers).length;
      
      // Matriz MAIS (Perfil Público): soma de +1 para cada escolha MAIS
      const scoresMais = { D: 0, I: 0, S: 0, C: 0 };
      
      // Matriz MENOS (Perfil Natural): soma de +1 para cada escolha MENOS
      const scoresMenos = { D: 0, I: 0, S: 0, C: 0 };
      
      // Para cada resposta, somar +1 no contador apropriado
      Object.values(answers).forEach((answer) => {
        if (answer.mais !== null) {
          const maisTrait = TRAIT_MAP[answer.mais];
          scoresMais[maisTrait] += 1;
        }
        if (answer.menos !== null) {
          const menosTrait = TRAIT_MAP[answer.menos];
          scoresMenos[menosTrait] += 1;
        }
      });
      
      // Matriz SÍNTESE (Perfil Composto): MAIS - MENOS
      const scoresSintese = {
        D: scoresMais.D - scoresMenos.D,
        I: scoresMais.I - scoresMenos.I,
        S: scoresMais.S - scoresMenos.S,
        C: scoresMais.C - scoresMenos.C
      };
      
      // ==========================================
      // PASSO 2: Normalização (0% a 100%)
      // Fórmula: %F = ((S_F - Mínimo) / (Máximo - Mínimo)) × 100
      // Com 40 questões: Mínimo = -40, Máximo = +40
      // ==========================================
      const minComposto = -totalQuestoes; // -40
      const maxComposto = totalQuestoes;   // +40
      const range = maxComposto - minComposto; // 80
      
      const normalizar = (score) => {
        return Math.round(((score - minComposto) / range) * 100);
      };
      
      // Percentuais normalizados para cada matriz
      const percentuaisMais = {
        D: Math.round((scoresMais.D / totalQuestoes) * 100),
        I: Math.round((scoresMais.I / totalQuestoes) * 100),
        S: Math.round((scoresMais.S / totalQuestoes) * 100),
        C: Math.round((scoresMais.C / totalQuestoes) * 100)
      };
      
      const percentuaisMenos = {
        D: Math.round((scoresMenos.D / totalQuestoes) * 100),
        I: Math.round((scoresMenos.I / totalQuestoes) * 100),
        S: Math.round((scoresMenos.S / totalQuestoes) * 100),
        C: Math.round((scoresMenos.C / totalQuestoes) * 100)
      };
      
      const percentuaisSintese = {
        D: normalizar(scoresSintese.D),
        I: normalizar(scoresSintese.I),
        S: normalizar(scoresSintese.S),
        C: normalizar(scoresSintese.C)
      };
      
      // ==========================================
      // PASSO 3: Índice de Inconsistência
      // Fatores opostos com scores altos = perfil inválido
      // D vs S (ambos ≥ 85%) ou I vs C (ambos ≥ 85%)
      // ==========================================
      const LIMIAR_INCONSISTENCIA = 85;
      let inconsistente = false;
      let motivoInconsistencia = null;
      
      if (percentuaisSintese.D >= LIMIAR_INCONSISTENCIA && percentuaisSintese.S >= LIMIAR_INCONSISTENCIA) {
        inconsistente = true;
        motivoInconsistencia = 'D e S são comportamentos estatisticamente excludentes';
      } else if (percentuaisSintese.I >= LIMIAR_INCONSISTENCIA && percentuaisSintese.C >= LIMIAR_INCONSISTENCIA) {
        inconsistente = true;
        motivoInconsistencia = 'I e C são comportamentos estatisticamente excludentes';
      }
      
      // ==========================================
      // PASSO 4: Determinar Perfil Dominante
      // Baseado no gráfico SÍNTESE (Perfil Composto)
      // ==========================================
      const sorted = Object.entries(percentuaisSintese).sort(([,a], [,b]) => b - a);
      const dominant = sorted[0][0];
      const secondary = sorted[1][0];
      
      // Determinar estilo combinado (se secundário >= 70% do dominante)
      let estilo = dominant;
      if (sorted[1][1] >= sorted[0][1] * 0.7 && sorted[0][1] > 0) {
        estilo = dominant + secondary;
      }
      
      // ==========================================
      // RESULTADO FINAL
      // ==========================================
      const calculatedResult = {
        // Scores brutos
        scoresMais,
        scoresMenos,
        scoresSintese,
        // Percentuais normalizados
        percentuaisMais,
        percentuaisMenos,
        percentuaisSintese,
        // Perfil identificado (baseado na síntese)
        dominant,
        secondary,
        estilo,
        // Validação
        inconsistente,
        motivoInconsistencia,
        // Metadados
        totalQuestoes,
        tipo: 'g2bp',
        versao: '2.0', // Versão atualizada com algoritmo correto
        algoritmo: 'disc_ipsativo_forced_choice',
        created_at: new Date().toISOString(),
        // Manter compatibilidade com versão anterior
        scores: scoresSintese,
        percentages: percentuaisSintese
      };
      
      setResult(calculatedResult);
      
      // Verificar se veio de um convite
      const inviteToken = sessionStorage.getItem('test_invite_token');
      
      if (inviteToken) {
        // Se veio de um convite, usar o endpoint específico que aceita qualquer role
        try {
          await testInvitesAPI.saveResult(inviteToken, {
            g2bp_result: calculatedResult
          });
          toast.success('Teste G2BP completado! A empresa foi notificada.');
          sessionStorage.removeItem('test_invite_token');
        } catch (e) {
          console.error('Erro ao salvar via convite:', e);
          toast.error('Erro ao salvar resultado');
          return;
        }
      } else {
        // Se não veio de convite, salvar no perfil do candidato diretamente
        try {
          await candidatesAPI.updateMe({
            g2bp_result: calculatedResult
          });
          toast.success('Perfil G2BP salvo com sucesso!');
        } catch (e) {
          console.error('Erro ao salvar no perfil:', e);
          toast.error('Erro ao salvar resultado');
          return;
        }
      }
      
    } catch (error) {
      console.error('Erro ao calcular resultado:', error);
      toast.error('Erro ao salvar resultado');
    } finally {
      setLoading(false);
    }
  };

  // Refazer teste
  const handleRetake = () => {
    setExistingResult(null);
    setResult(null);
    setAnswers({});
    setCurrentQuestion(0);
  };

  // Loading inicial
  if (checkingExisting) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Mostrar resultado existente
  if (existingResult && !result) {
    const profile = DISC_PROFILES[existingResult.dominant];
    const isV2 = existingResult.versao === '2.0';
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-900 to-indigo-700 p-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/m')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-white">Seu Perfil G2BP</h1>
            <div className="w-10" />
          </div>

          {/* Resultado */}
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            {/* Alerta de inconsistência se aplicável */}
            {existingResult.inconsistente && (
              <InconsistenciaAlert motivo={existingResult.motivoInconsistencia} />
            )}
            
            <div className="text-center mb-6">
              <div className={`w-20 h-20 ${TRAIT_COLORS[existingResult.dominant].bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <span className="text-3xl font-bold text-white">{existingResult.dominant}</span>
              </div>
              <h2 className="text-2xl font-bold text-slate-800">{profile.nome}</h2>
              <p className="text-slate-600 mt-2">{profile.descricao}</p>
              {existingResult.estilo && existingResult.estilo.length > 1 && (
                <Badge className="mt-2 bg-indigo-100 text-indigo-700">
                  Estilo: {existingResult.estilo}
                </Badge>
              )}
            </div>

            {/* Gráficos - Versão 2.0 com 3 matrizes */}
            {isV2 && existingResult.percentuaisSintese ? (
              <div className="space-y-6 mb-6">
                {/* Gráfico Síntese (Principal) */}
                <div className="bg-indigo-50 rounded-lg p-4">
                  <h3 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Perfil Composto (Síntese)
                  </h3>
                  <G2BPBars percentages={existingResult.percentuaisSintese} />
                </div>
                
                {/* Gráficos Mais e Menos lado a lado */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-lg p-3">
                    <G2BPBars 
                      percentages={existingResult.percentuaisMais} 
                      label="Perfil Público (MAIS)"
                      compact
                    />
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <G2BPBars 
                      percentages={existingResult.percentuaisMenos} 
                      label="Perfil Natural (MENOS)"
                      compact
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* Fallback para versão 1.0 */
              <div className="mb-6">
                <h3 className="font-semibold text-slate-700 mb-3">Seu Perfil Comportamental</h3>
                <G2BPBars percentages={existingResult.percentages || existingResult.percentuaisSintese} />
              </div>
            )}

            {/* Características */}
            <div className="mb-6">
              <h3 className="font-semibold text-slate-700 mb-2">Características Principais</h3>
              <div className="flex flex-wrap gap-2">
                {profile.caracteristicas.map((carac, i) => (
                  <Badge key={i} variant="outline" className={TRAIT_COLORS[existingResult.dominant].border}>
                    {carac}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Ações */}
            <div className="space-y-3">
              <Button 
                onClick={handleRetake}
                variant="outline"
                className="w-full"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Refazer Avaliação
              </Button>
              <Button 
                onClick={() => navigate('/m')}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                Voltar ao Início
              </Button>
            </div>
          </div>

          {/* Branding */}
          <div className="text-center mt-6">
            <p className="text-indigo-200 text-sm">G2 Behavioral Profile</p>
            <p className="text-indigo-300 text-xs">by G2eGente • v{existingResult.versao || '1.0'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Mostrar resultado calculado
  if (result) {
    const profile = DISC_PROFILES[result.dominant];
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-900 to-indigo-700 p-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="w-10" />
            <h1 className="text-lg font-bold text-white">Resultado G2BP</h1>
            <div className="w-10" />
          </div>

          {/* Resultado */}
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            {/* Alerta de inconsistência se aplicável */}
            {result.inconsistente && (
              <InconsistenciaAlert motivo={result.motivoInconsistencia} />
            )}
            
            <div className="text-center mb-6">
              <Award className="w-12 h-12 text-amber-500 mx-auto mb-2" />
              <p className="text-slate-600 text-sm mb-4">Avaliação Concluída!</p>
              
              <div className={`w-24 h-24 ${TRAIT_COLORS[result.dominant].bg} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                <span className="text-4xl font-bold text-white">{result.dominant}</span>
              </div>
              <h2 className="text-2xl font-bold text-slate-800">{profile.nome}</h2>
              <p className="text-slate-600 mt-2">{profile.descricao}</p>
              {result.estilo && result.estilo.length > 1 && (
                <Badge className="mt-3 bg-indigo-100 text-indigo-700 text-base px-4 py-1">
                  Estilo: {result.estilo}
                </Badge>
              )}
            </div>

            {/* Gráfico Principal - Síntese */}
            <div className="bg-indigo-50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                <Award className="w-4 h-4" />
                Perfil Composto (Síntese)
              </h3>
              <G2BPBars percentages={result.percentuaisSintese} />
            </div>

            {/* Gráficos Mais e Menos */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-green-50 rounded-lg p-3">
                <G2BPBars 
                  percentages={result.percentuaisMais} 
                  label="Perfil Público"
                  compact
                />
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <G2BPBars 
                  percentages={result.percentuaisMenos} 
                  label="Perfil Natural"
                  compact
                />
              </div>
            </div>

            {/* Percentuais do Gráfico Síntese */}
            <div className="grid grid-cols-4 gap-2 mb-6">
              {['D', 'I', 'S', 'C'].map((trait) => (
                <div key={trait} className={`text-center p-3 rounded-lg ${TRAIT_COLORS[trait].light}`}>
                  <span className={`text-2xl font-bold ${TRAIT_COLORS[trait].text}`}>
                    {result.percentuaisSintese[trait]}%
                  </span>
                  <p className="text-xs text-slate-600 mt-1">{DISC_PROFILES[trait].nome}</p>
                </div>
              ))}
            </div>

            {/* Ações */}
            <Button 
              onClick={() => navigate('/m')}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Concluir
            </Button>
          </div>

          {/* Branding */}
          <div className="text-center mt-6">
            <p className="text-indigo-200 text-sm">G2 Behavioral Profile</p>
            <p className="text-indigo-300 text-xs">by G2eGente • v{result.versao}</p>
          </div>
        </div>
      </div>
    );
  }

  // Questionário
  const question = G2BP_QUESTIONS[currentQuestion];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-indigo-900 text-white p-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/m')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <h1 className="text-lg font-bold">G2 Behavioral Profile</h1>
              <p className="text-xs text-indigo-200">Avaliação Comportamental</p>
            </div>
            <div className="w-10" />
          </div>
          
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Questão {currentQuestion + 1} de {totalQuestions}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-indigo-700" />
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-md mx-auto p-4">
        {/* Instruções */}
        <div className="bg-indigo-50 rounded-lg p-3 mb-4 border border-indigo-200">
          <p className="text-sm text-indigo-800">
            <strong>Selecione</strong> a palavra que <span className="text-green-600 font-semibold">MAIS</span> combina com você 
            e a que <span className="text-red-600 font-semibold">MENOS</span> combina.
          </p>
        </div>

        {/* Questão */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="text-center mb-4">
            <Badge variant="outline" className="text-indigo-600 border-indigo-300">
              Questão {question.id}
            </Badge>
          </div>

          {/* Palavras */}
          <div className="space-y-3">
            {question.words.map((word, index) => {
              const isMais = currentAnswer.mais === index;
              const isMenos = currentAnswer.menos === index;
              const isDisabledForMais = currentAnswer.menos === index;
              const isDisabledForMenos = currentAnswer.mais === index;
              
              return (
                <div 
                  key={index}
                  className={`
                    p-4 rounded-lg border-2 transition-all
                    ${isMais ? 'border-green-500 bg-green-50' : ''}
                    ${isMenos ? 'border-red-500 bg-red-50' : ''}
                    ${!isMais && !isMenos ? 'border-slate-200 bg-white' : ''}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-lg font-medium ${isMais ? 'text-green-700' : isMenos ? 'text-red-700' : 'text-slate-700'}`}>
                      {word}
                    </span>
                    
                    <div className="flex gap-2">
                      {/* Botão MAIS */}
                      <button
                        onClick={() => handleSelectWord('mais', index)}
                        disabled={isDisabledForMais}
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center transition-all
                          ${isMais 
                            ? 'bg-green-500 text-white' 
                            : isDisabledForMais
                              ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                              : 'bg-green-100 text-green-600 hover:bg-green-200'
                          }
                        `}
                        title="Mais combina comigo"
                      >
                        <ThumbsUp className="w-5 h-5" />
                      </button>
                      
                      {/* Botão MENOS */}
                      <button
                        onClick={() => handleSelectWord('menos', index)}
                        disabled={isDisabledForMenos}
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center transition-all
                          ${isMenos 
                            ? 'bg-red-500 text-white' 
                            : isDisabledForMenos
                              ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                              : 'bg-red-100 text-red-600 hover:bg-red-200'
                          }
                        `}
                        title="Menos combina comigo"
                      >
                        <ThumbsDown className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legenda */}
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-1 text-green-600">
              <ThumbsUp className="w-4 h-4" />
              <span>Mais</span>
            </div>
            <div className="flex items-center gap-1 text-red-600">
              <ThumbsDown className="w-4 h-4" />
              <span>Menos</span>
            </div>
          </div>
        </div>

        {/* Navegação */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="flex-1"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Anterior
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={!canProceed || loading}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calculando...
              </>
            ) : currentQuestion === totalQuestions - 1 ? (
              <>
                Finalizar
                <CheckCircle className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Próxima
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Aviso */}
        {!canProceed && (
          <p className="text-center text-sm text-amber-600 mt-3">
            Selecione uma palavra MAIS e uma MENOS para continuar
          </p>
        )}
      </div>
    </div>
  );
}
